import { Alert, Stack } from "@mui/material"
import { useEffect, useRef } from "react"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { Case, If, Switch } from "lib/@components/switch"
import { ProgressCard } from "routes/asset-register/plugins/asset-mapping/plugins/progress-card/progress-card"
import { useProgressCard } from "routes/asset-register/plugins/asset-mapping/plugins/progress-card/use-progress-card"
import { registerMobiliserStage } from "routes/asset-register/plugins/asset-mapping/controller/register-mobiliser-stage"
import { getMappingFreshness } from "routes/asset-register/plugins/asset-mapping/controller/get-mapping-freshness"
import { noChange, useRefresh } from "lib/@hooks/useRefresh"
import { useInterval } from "lib/@hooks/useInterval"
import { TypeProcessingRetry } from "routes/asset-register/plugins/asset-mapping/type-processing-retry"
import { MOBILISER_STAGE_PROCESSING_TYPES } from "./mobiliser-stage.mjs"
import { refreshId } from "lib/graphql/api"
import { invalidate } from "lib/graphql/cache"
import { Loader } from "lib/@components/Loader"
import noop from "lib/noop"

const STAGE_RECHECK_INTERVAL_MS = 5000
const PROCESSING_WINDOW_MS = 1000 * 60 * 30
const OVERDUE_RECHECK_INTERVAL_MS = 1000 * 60

export function TypeProcessingStage() {
    const { target, refreshRegister = noop } = useBoundContext()
    const registerId = target?._id ?? target?.id
    const persistedStage = registerMobiliserStage.useResults(refreshId(registerId))
    const freshness = getMappingFreshness.useResults(refreshId(registerId))
    const refresh = useRefresh(noChange)
    const hasMovedOn = !!persistedStage && persistedStage !== MOBILISER_STAGE_PROCESSING_TYPES
    const progressCard = useProgressCard(target, { hasMovedOn })
    const refreshRegisterRef = useRef(refreshRegister)
    refreshRegisterRef.current = refreshRegister
    const hasMovedOnRef = useRef(hasMovedOn)
    hasMovedOnRef.current = hasMovedOn

    const processingState = getProcessingState(progressCard)
    const processingOverdue =
        processingState === "waiting" && freshness != null && isPastProcessingWindow(freshness.lastMappedAt)

    useEffect(recheckPersistedStage, [])
    useEffect(followPersistedStage, [hasMovedOn])
    useInterval(refresh, OVERDUE_RECHECK_INTERVAL_MS)

    return (
        <Stack sx={{ mt: 2 }} spacing={2} data-cy="type-processing-stage">
            <Switch value={processingState}>
                <Case when="busy">
                    <ProgressCard
                        progress={progressCard.progress}
                        elapsedTime={progressCard.elapsedTime}
                        estimatedTime={progressCard.estimatedTime}
                        processedText={progressCard.processedText}
                        title={progressCard.title}
                        phaseText={progressCard.phaseText}
                        dataCy="refining-types-busy"
                    />
                </Case>
                <Case when="failed">
                    <TypeProcessingRetry
                        severity="error"
                        title="Asset type processing failed"
                        message={`${progressCard.error} Retry to process the asset types again, or change the mapping settings and apply them.`}
                        dataCy="type-processing-failed"
                    />
                </Case>
                <Case when="waiting">
                    <Loader inline color="primary" caption="Preparing asset types for review..." />
                    <If value={processingOverdue}>
                        <TypeProcessingRetry
                            severity="info"
                            title="Taking longer than expected?"
                            message="If this register was left processing, retry to build its asset types again."
                            dataCy="type-processing-retry"
                        />
                    </If>
                </Case>
            </Switch>
            <If value={processingState !== "failed"}>
                <Alert severity="info">
                    This may take a while for large registers. You can safely navigate away and return later. Processing
                    will continue in the background.
                </Alert>
            </If>
        </Stack>
    )

    function recheckPersistedStage() {
        const timer = setInterval(rereadStage, STAGE_RECHECK_INTERVAL_MS)
        return function stopRechecking() {
            clearInterval(timer)
        }

        function rereadStage() {
            invalidate("registerMobiliserStage")
            if (hasMovedOnRef.current) refreshRegisterRef.current()
        }
    }

    function followPersistedStage() {
        if (!hasMovedOn) return
        refreshRegisterRef.current()
    }
}

function isPastProcessingWindow(lastMappedAt) {
    if (!lastMappedAt) return true
    return Date.now() - new Date(lastMappedAt).getTime() > PROCESSING_WINDOW_MS
}

function getProcessingState({ isBusy, error }) {
    if (isBusy) return "busy"
    if (error) return "failed"
    return "waiting"
}
